import React from 'react';
import { Link } from 'react-router-dom';
import { AppIcon } from './AppIcon.js';
import { Pay888MarkSVG } from './AppLogo.js';
import { CardNetwork } from './CardNetwork.js';
import { useCardDisplayName } from '../features/auth/useCardDisplayName.js';

export const CardArtwork: React.FC<{
  readonly frozen?: boolean;
  readonly network?: 'visa' | 'mastercard';
  readonly showEdition?: boolean;
  readonly cardholderName?: string;
}> = ({ frozen = false, network = 'visa', showEdition = false, cardholderName = 'Your Name' }) => (
  <div
    className={`pay-card${frozen ? ' pay-card-frozen' : ''}`}
    aria-label={frozen ? 'Frozen sample card' : 'PAY888 sample card'}
  >
    <div className="pay-card-top">
      <span>
        <Pay888MarkSVG size={38} onCard />
      </span>
      {(frozen || showEdition) && (
        <span className="card-edition">{frozen ? 'FROZEN · SAMPLE' : 'VIRTUAL · SAMPLE'}</span>
      )}
    </div>
    <div className="card-orbit" aria-hidden="true" />
    <div className="card-hardware" aria-hidden="true">
      <div className="card-chip">
        <i />
        <i />
        <i />
      </div>
      <svg
        className="card-contactless"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      >
        <path d="M8 9a6 6 0 0 1 0 6M12 6a10 10 0 0 1 0 12M16 3a15 15 0 0 1 0 18" />
      </svg>
    </div>
    <div className="pay-card-number">•••• &nbsp; •••• &nbsp; •••• &nbsp; 8888</div>
    <div className="pay-card-bottom">
      <div className="card-details">
        <span className="card-holder">
          <small>CARDHOLDER</small>
          <strong dir="auto" title={cardholderName.trim() || 'Your Name'}>
            {cardholderName.trim() || 'Your Name'}
          </strong>
        </span>
        <span>
          <small>VALID THRU</small>
          <strong>MM/YY</strong>
        </span>
        <span>
          <small>CVV</small>
          <strong>•••</strong>
        </span>
      </div>
      <CardNetwork network={network} />
    </div>
  </div>
);

export const WalletOverview: React.FC<{
  readonly preview?: boolean;
  readonly onDeposit: () => void;
}> = ({ preview = false, onDeposit }) => {
  const cardholderName = useCardDisplayName();
  return (
    <div className="wallet-grid">
      <section className="panel balance-panel">
        <div className="section-heading">
          <span className="muted">Available balance</span>
          <span className="subtle-pill">{preview ? 'Example · USD' : 'Not connected'}</span>
        </div>
        <div className="balance-value">
          {preview ? '$1,250' : '—'}
          {preview && <span>.00</span>}
        </div>
        <p className="balance-caption">
          {preview
            ? 'Sample balance for testing the preview.'
            : 'Your balance will appear once wallet services are available.'}
        </p>
        <div className="wallet-actions">
          <button className="ios-btn-primary" onClick={onDeposit}>
            <AppIcon name="plus" />
            Add money
          </button>
          <Link className="ios-btn-secondary" to={preview ? '/preview/cards' : '/cards'}>
            <AppIcon name="card" />
            Cards
          </Link>
        </div>
        <div className="balance-footnote">
          <AppIcon name="wallet" />
          <span>{preview ? 'Demo wallet · No real funds' : 'Funding is not available yet'}</span>
        </div>
      </section>
      <section className="panel card-panel">
        <div className="section-heading">
          <h2>{preview ? 'Your card' : 'Card preview'}</h2>
          <Link to={preview ? '/preview/cards' : '/cards'}>View cards ↗</Link>
        </div>
        <CardArtwork cardholderName={cardholderName} />
        <div className="card-caption">
          <span>{preview ? 'Virtual card' : 'Card design preview'}</span>
          <span className="muted">{preview ? 'Sample card' : 'Coming soon'}</span>
        </div>
      </section>
    </div>
  );
};
